import {
  getAuthLocaleStorage,
  removeAuthLocalStorage,
  setAuthLocaleStorage,
} from 'src/utils'
import axios from 'axios'
import dayjs from 'dayjs'

const isTokenExpired = (expirationTime: string) => {
  if (!expirationTime) return true
  const currentTime = dayjs()
  return currentTime.isAfter(expirationTime)
}

// Function to refresh the access token
const refreshAccessToken = async () => {
  const { refreshToken, refreshExpiration } = getAuthLocaleStorage()

  try {
    // Check if the refresh token is expired
    if (isTokenExpired(refreshExpiration)) {
      removeAuthLocalStorage()
      window.location.href = `${window.location.protocol}//${window.location.host}/login`
      return
    }
    //if not expired, return the new access token
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/refresh-token`,
      { refreshToken: refreshToken }
    )
    const { accessToken, accessExpiration } = response.data

    setAuthLocaleStorage({ accessToken, accessExpiration })
    return accessToken
  } catch (error) {
    console.error('Failed to refresh access token', error)
    removeAuthLocalStorage()
    window.location.href = `${window.location.protocol}//${window.location.host}/login`
    throw error
  }
}

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL,
})

// Request interceptor to add the access token
axiosInstance.interceptors.request.use(
  async (config) => {
    const { accessToken, accessExpiration } = getAuthLocaleStorage()

    // Check if we have access token
    if (accessExpiration) {
      // Check if the access token is expired
      if (isTokenExpired(accessExpiration)) {
        // Refresh the access token
        const newAccessToken = await refreshAccessToken()
        config.headers['Authorization'] = `Bearer ${newAccessToken}`
      } else config.headers['Authorization'] = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const newAccessToken = await refreshAccessToken()
        axios.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${newAccessToken}`
        return axiosInstance(originalRequest)
      } catch (refreshError) {
        console.error('Failed to refresh access token', refreshError)
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default axiosInstance
